import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { Warning as WarningIcon } from "@material-ui/icons";

import { SERVER } from "../../../constants";
import { useTranslation } from "../../../localization";

// Luxon
const { DateTime } = require("luxon");

function fmtScore(pred) {
  return pred.home_score + " - " + pred.away_score;
}

function kickoff(fixture) {
  return DateTime.fromISO(fixture.date, { zone: "utc" })
    .toLocal()
    .toFormat("M/d h:mm a");
}

// A target group's stored prediction for a fixture differs from the one we're
// about to copy (i.e. copying would overwrite a real, different value).
function differs(a, b) {
  if (!a || !b) {
    return false;
  }
  return (
    String(a.home_score) !== String(b.home_score) ||
    String(a.away_score) !== String(b.away_score)
  );
}

// Dialog to copy the current user's predictions (for the open league) into other
// groups they belong to that also have this league. Shows exactly what will be
// copied, lets the user pick target group(s), flags per-match conflicts (a
// target already has a different prediction) and lets the user opt in/out per
// match before writing.
export default function CopyPredictionsDialog({
  open,
  onClose,
  league,
  userName,
  targetGroups,
  copyItems,
  skippedCount,
  onCopied,
}) {
  var t = useTranslation();

  // group name -> { fixtureId: { home_score, away_score } } for each selected target
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [existingPreds, setExistingPreds] = useState({});
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  // Manual per-match include overrides (fixtureId -> bool). Items not present
  // fall back to their default (off when conflicting, on otherwise), so target
  // changes don't clobber an explicit choice.
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset everything whenever the dialog is (re)opened for a new selection.
  useEffect(() => {
    if (open) {
      setSelectedTargets([]);
      setExistingPreds({});
      setOverrides({});
      setSaving(false);
    }
  }, [open]);

  // Fetch existing predictions for any newly selected target group (each group's
  // map is fetched once and cached for the dialog's lifetime).
  useEffect(() => {
    var missing = selectedTargets.filter(g => !(g in existingPreds));
    if (missing.length === 0) {
      return;
    }
    setLoadingConflicts(true);
    Promise.all(
      missing.map(group => {
        var url = new URL(SERVER + "/predictions");
        url.search = new URLSearchParams({
          group,
          league,
          name: userName,
        }).toString();
        return fetch(url, { cache: "no-store" })
          .then(r => r.json())
          .then(json => [group, json || {}])
          .catch(() => [group, {}]);
      }),
    ).then(entries => {
      setExistingPreds(prev => {
        var next = { ...prev };
        for (const [group, map] of entries) {
          next[group] = map;
        }
        return next;
      });
      setLoadingConflicts(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargets]);

  function toggleTarget(group) {
    setSelectedTargets(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group],
    );
  }

  // The selected targets that already hold a *different* prediction for this
  // match, with their existing value (used to flag conflicts in the list).
  function conflictsFor(item) {
    return selectedTargets
      .map(group => ({ group, existing: (existingPreds[group] || {})[item.id] }))
      .filter(c => differs(c.existing, item));
  }

  function included(item) {
    if (item.id in overrides) {
      return overrides[item.id];
    }
    // Everything is included by default; conflicting matches are flagged so the
    // user can opt out, but copying overwrites them unless they uncheck.
    return true;
  }

  function toggleInclude(item) {
    var current = included(item);
    setOverrides(prev => ({ ...prev, [item.id]: !current }));
  }

  var includedItems = copyItems.filter(included);
  var canCopy =
    selectedTargets.length > 0 &&
    includedItems.length > 0 &&
    !loadingConflicts &&
    !saving;

  function handleCopy() {
    setSaving(true);
    var predictions = {};
    includedItems.forEach(item => {
      predictions[item.id] = {
        home_score: item.home_score,
        away_score: item.away_score,
      };
    });
    Promise.all(
      selectedTargets.map(group =>
        fetch(SERVER + "/predictions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            group,
            league,
            name: userName,
            predictions,
          }),
        }).then(r => r.json()),
      ),
    ).then(
      () => {
        setSaving(false);
        onCopied();
      },
      error => {
        console.debug(error);
        setSaving(false);
        alert(t.fixtures.couldNotCopy);
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t.fixtures.copyTitle}</DialogTitle>
      <DialogContent>
        {copyItems.length === 0 ? (
          <Typography color="textSecondary">
            {t.fixtures.nothingToCopy}
          </Typography>
        ) : targetGroups.length === 0 ? (
          <Typography color="textSecondary">
            {t.fixtures.noEligibleGroups}
          </Typography>
        ) : (
          <>
            {skippedCount > 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 8 }}
              >
                {t.fixtures.copySkipped(skippedCount)}
              </Typography>
            ) : null}

            <Typography variant="subtitle2" gutterBottom>
              {t.fixtures.copyTargetLabel}
            </Typography>
            <div style={{ marginBottom: 8 }}>
              {targetGroups.map(group => (
                <FormControlLabel
                  key={group.name}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedTargets.includes(group.name)}
                      onChange={() => toggleTarget(group.name)}
                    />
                  }
                  label={group.name}
                />
              ))}
              {loadingConflicts ? (
                <CircularProgress size={16} style={{ marginLeft: 8 }} />
              ) : null}
            </div>

            <Divider />

            <List dense>
              {copyItems.map(item => {
                var conflicts = conflictsFor(item);
                return (
                  <ListItem key={item.id} disableGutters divider>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={included(item)}
                        onChange={() => toggleInclude(item)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        item.fixture.home_team +
                        " vs " +
                        item.fixture.away_team +
                        "  (" +
                        fmtScore(item) +
                        ")"
                      }
                      secondary={
                        <>
                          {kickoff(item.fixture)}
                          {conflicts.map(c => (
                            <span
                              key={c.group}
                              style={{
                                display: "block",
                                color: "#c77700",
                              }}
                            >
                              <WarningIcon
                                style={{
                                  fontSize: 14,
                                  verticalAlign: "text-bottom",
                                  marginRight: 4,
                                }}
                              />
                              {c.group}: {t.fixtures.copyConflict(fmtScore(c.existing))}
                            </span>
                          ))}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          {t.common.cancel}
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          color="primary"
          disabled={!canCopy}
        >
          {saving ? <CircularProgress size={20} /> : t.fixtures.copyToGroup}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
